import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./icons";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO, WHATSAPP_URL } from "@/constants/site";

const CARDS = [
    { icon: Phone, title: "Téléphone", text: PHONE_DISPLAY, href: PHONE_TEL, testid: "contact-card-phone" },
    { icon: WhatsAppIcon, title: "WhatsApp", text: "Discuter directement", href: WHATSAPP_URL, external: true, testid: "contact-card-whatsapp" },
    { icon: Mail, title: "Email", text: EMAIL, href: EMAIL_MAILTO, testid: "contact-card-email" },
    { icon: MapPin, title: "Zone d'intervention", text: "Toute la France", testid: "contact-card-zone" },
    { icon: Clock, title: "Horaires", text: "7j/7 de 8h à 20h", testid: "contact-card-hours" },
];

const CardInner = ({ card }) => (
    <>
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-midnight-800 text-midnight-800 transition-[transform,border-color,color] duration-500 group-hover:scale-110 group-hover:border-gold-dark group-hover:text-gold-dark">
            <card.icon size={24} />
        </span>
        <h3 className="mt-5 font-display font-extrabold uppercase tracking-wide text-sm text-midnight-900">{card.title}</h3>
        <p className="mt-1.5 text-sm text-gray-500">{card.text}</p>
    </>
);

export const Contact = () => (
    <section id="contact" data-testid="contact-section" className="relative bg-white py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal className="text-center max-w-2xl mx-auto">
                <span className="text-xs font-bold tracking-[0.3em] uppercase text-gold-dark">Contact</span>
                <h2 className="mt-4 font-display font-extrabold uppercase tracking-wide text-3xl sm:text-4xl text-midnight-900">
                    Une question, un devis ?
                </h2>
                <span className="mt-6 mx-auto block h-px w-24 bg-gradient-to-r from-transparent via-gold to-transparent" />
                <p className="mt-6 text-sm sm:text-base text-gray-500">
                    Contactez-nous par téléphone ou par email, nous vous répondons rapidement.
                </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {CARDS.map((card, i) => (
                    <Reveal key={card.testid} delay={i * 0.08} className="h-full">
                        {card.href ? (
                            <a
                                data-testid={card.testid}
                                href={card.href}
                                {...(card.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                className="group block h-full rounded-2xl border border-black/5 bg-[#F7F5F1] p-8 text-center shadow-[0_10px_40px_rgba(10,14,23,0.06)] transition-[border-color,background-color,transform] duration-500 hover:border-gold/60 hover:bg-white hover:-translate-y-1.5"
                            >
                                <CardInner card={card} />
                            </a>
                        ) : (
                            <div
                                data-testid={card.testid}
                                className="group h-full rounded-2xl border border-black/5 bg-[#F7F5F1] p-8 text-center shadow-[0_10px_40px_rgba(10,14,23,0.06)]"
                            >
                                <CardInner card={card} />
                            </div>
                        )}
                    </Reveal>
                ))}

                <Reveal delay={0.4} className="h-full">
                    <div className="h-full rounded-2xl border border-gold/40 bg-midnight-800 p-8 flex flex-col items-center justify-center gap-4 text-center shadow-[0_25px_60px_rgba(10,14,23,0.25)]">
                        <a
                            data-testid="contact-whatsapp-cta"
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2.5 rounded-lg bg-[#25D366] px-7 py-3.5 font-bold uppercase tracking-wide text-sm text-midnight-900 transition-transform duration-300 hover:scale-105 active:scale-95"
                        >
                            <WhatsAppIcon size={19} />
                            Écrire sur WhatsApp
                        </a>
                        <a
                            data-testid="contact-phone-cta"
                            href={PHONE_TEL}
                            className="inline-flex items-center gap-2.5 rounded-lg bg-gradient-to-r from-gold-dark via-gold to-gold-light px-7 py-3.5 font-bold uppercase tracking-wide text-sm text-midnight-900 transition-transform duration-300 hover:scale-105 active:scale-95"
                        >
                            <Phone size={18} strokeWidth={2.5} />
                            {PHONE_DISPLAY}
                        </a>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);
